import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Share2, Check, RefreshCw } from 'lucide-react';
import { referralAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import { isIOS } from '../services/native';

const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export default function ReferralPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { hidePaymentUI } = useSubscription();

  // Block access: admin flag (hidePaymentUI) OR native iOS app OR any iOS browser
  useEffect(() => {
    if (hidePaymentUI || isIOS || isIOSDevice) {
      navigate('/', { replace: true });
    }
  }, [hidePaymentUI, navigate]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [copied, setCopied] = useState(false);


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [codeRes, dashRes] = await Promise.all([
        referralAPI.getMyCode(),
        referralAPI.getDashboard()
      ]);
      setCodeData(codeRes.data.data);
      setDashboard(dashRes.data.data);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const referralLink = codeData?.referralCode
    ? `${codeData.referralBaseUrl || window.location.origin}/register?ref=${codeData.referralCode}`
    : '';

  const getShareText = () => {
    if (!codeData || !referralLink) return '';
    const template = codeData.shareMessageTemplate || 'Join me on Hisab Journey! Use my referral link to sign up: {link}';
    if (template.includes('{link}')) return template.replace(/\{link\}/g, referralLink);
    const urlReplaced = template.replace(/https?:\/\/[^\s]+/g, referralLink);
    if (urlReplaced !== template) return urlReplaced;
    return `${template} ${referralLink}`;
  };

  const handleCopyLink = async () => {
    const text = getShareText();
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleWhatsAppShare = () => {
    if (!referralLink) return;
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
      </div>
    );
  }

  if (!dashboard?.enabled) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-lg font-semibold text-surface-900">Referrals</h1>
          <p className="text-xs text-surface-500">Refer friends and earn rewards</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-8 text-center">
          <span className="text-4xl block mb-3">🎁</span>
          <p className="text-surface-500 text-sm">The referral program is currently not available.</p>
        </div>
      </div>
    );
  }

  const { stats, progress, reward, referrals } = dashboard;
  const progressPercent = progress.required > 0
    ? Math.min(100, Math.round((progress.qualified / progress.required) * 100))
    : 0;
  const remaining = Math.max(0, progress.required - progress.qualified);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="xl:hidden">
        <h1 className="text-lg font-semibold text-surface-900">Referrals</h1>
        <p className="text-xs text-surface-500">Refer friends and earn rewards</p>
      </div>
      <div className="hidden xl:block page-header">
        <h1 className="page-title">Referrals</h1>
        <p className="page-subtitle">Refer friends and earn rewards</p>
      </div>

      {/* Share Card — Code + Buttons */}
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#ede9fe' }}>
            <span className="text-lg">🎁</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-surface-900">Your Referral Code</p>
            <p className="text-[11px] text-surface-400">Share with friends to earn rewards</p>
          </div>
        </div>

        {/* Code display */}
        <div className="flex items-center justify-center my-4">
          <div
            className="text-2xl font-bold tracking-[0.2em] px-6 py-2.5 rounded-xl"
            style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
          >
            {codeData?.referralCode || '---'}
          </div>
        </div>

        {/* Referral link */}
        {referralLink && (
          <div className="bg-surface-50 rounded-xl px-3 py-2 mb-4">
            <p className="text-[11px] text-surface-400 break-all font-mono">{referralLink}</p>
          </div>
        )}

        {/* Share buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleCopyLink}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-[13px] transition-colors"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={handleWhatsAppShare}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-[13px] text-white transition-colors"
            style={{ background: '#25D366' }}
          >
            <Share2 className="w-4 h-4" />
            WhatsApp
          </button>
        </div>
      </div>

      {/* Progress + Reward + Stats — Combined Card */}
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#fef9c3' }}>
            <span className="text-lg">🏆</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-surface-900">Progress & Reward</p>
            <p className="text-[11px] text-surface-400">
              {reward.earned ? 'Reward unlocked!' : `${remaining} more to unlock reward`}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 mb-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold" style={{ color: 'var(--accent-bg)' }}>{progress.qualified}</span>
              <span className="text-surface-400 text-sm">/ {progress.required}</span>
            </div>
            <span className="text-[11px] font-semibold text-surface-400">{progressPercent}%</span>
          </div>
          <div className="w-full bg-surface-100 rounded-full h-2.5">
            <div
              className="h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(progressPercent, 2)}%`, backgroundColor: progressPercent >= 100 ? '#16a34a' : 'var(--accent-bg)' }}
            />
          </div>
          <p className="text-[11px] text-surface-400 mt-1.5">
            Each friend must add {progress.minTransactions}+ transactions to qualify
          </p>
        </div>

        {/* Reward */}
        <div
          className="flex items-center gap-3 p-3 rounded-xl mb-4"
          style={reward.earned
            ? { background: '#f0fdf4', border: '1px solid #bbf7d0' }
            : { background: '#f8fafc', border: '1px solid #e2e8f0' }
          }
        >
          <span className="text-xl">{reward.earned ? '🎉' : '🎯'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold" style={{ color: reward.earned ? '#166534' : '#334155' }}>
              {reward.earned ? 'Reward earned!' : `Refer ${remaining} more to earn`}
            </p>
            <p className="text-[11px]" style={{ color: reward.earned ? '#16a34a' : '#94a3b8' }}>
              {reward.description}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Total', value: stats.total, emoji: '👥', bg: '#f1f5f9' },
            { label: 'Qualified', value: stats.qualified, emoji: '✅', bg: '#f0fdf4' },
            { label: 'Pending', value: stats.pending, emoji: '⏳', bg: '#fffbeb' }
          ].map(s => (
            <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: s.bg }}>
              <span className="text-sm">{s.emoji}</span>
              <p className="text-lg font-bold text-surface-900 mt-0.5">{s.value}</p>
              <p className="text-[10px] font-medium text-surface-400">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Referral List */}
      {referrals && referrals.length > 0 && (
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-[13px] font-semibold text-surface-900 mb-3">Your Referrals</p>
          <div className="space-y-2">
            {referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                    style={{ background: r.status === 'qualified' ? '#16a34a' : '#f59e0b' }}
                  >
                    {r.refereeName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-surface-900">{r.refereeName}</p>
                    <p className="text-[10px] text-surface-400">
                      {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  r.status === 'qualified'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state for referrals */}
      {(!referrals || referrals.length === 0) && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6 text-center">
          <span className="text-3xl block mb-2">👋</span>
          <p className="text-[13px] font-semibold text-surface-900 mb-1">No referrals yet</p>
          <p className="text-[11px] text-surface-400">Share your code with friends to get started!</p>
        </div>
      )}
    </div>
  );
}
